import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import LoadingScreen from '../ui/LoadingScreen';
import { useEffect, useState } from 'react';

type RoleBasedRouteProps = {
  role: 'admin' | 'formateur' | 'etudiant';
};

const RoleBasedRoute = ({ role }: RoleBasedRouteProps) => {
  const { userRole, isLoading, user } = useAuth();
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [authTimeout, setAuthTimeout] = useState(false);
  
  // Add a slight delay before showing loading screen to prevent flashes
  useEffect(() => {
    let timer: number | undefined;
    
    if (isLoading) {
      timer = window.setTimeout(() => {
        setShowLoadingScreen(true);
      }, 300); // Small delay to prevent flashing
    } else {
      setShowLoadingScreen(false);
      setAuthTimeout(false); // Reset timeout flag when loading completes
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isLoading]);

  // Force resolve loading after timeout to prevent infinite loading state
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (showLoadingScreen) {
        console.warn('Loading timeout reached in RoleBasedRoute, forcing navigation decision');
        setAuthTimeout(true);
        setShowLoadingScreen(false);
      }
    }, 8000); // Increased timeout for better reliability

    return () => clearTimeout(timeout);
  }, [showLoadingScreen]);

  // If we're showing loading screen, render it
  if (showLoadingScreen) {
    return <LoadingScreen />;
  }

  // In case of a timeout, check sessionStorage for stored role
  if (authTimeout) {
    try {
      const storedRole = sessionStorage.getItem('userRole');
      
      // If the stored role matches the required role, proceed
      if (storedRole === role) {
        console.log('Auth timeout but matching role found in storage - allowing access');
        return <Outlet />;
      }
      
      // Otherwise, redirect based on stored role
      if (storedRole === 'admin') {
        return <Navigate to="/admin" replace />;
      } else if (storedRole === 'formateur') {
        return <Navigate to="/trainer" replace />;
      } else if (storedRole === 'etudiant') {
        return <Navigate to="/student" replace />;
      }
      
      // For production deploy-specific recovery from localStorage
      if (import.meta.env.PROD) {
        try {
          const hasLocalSession = localStorage.getItem('sb-supabase-auth-token');
          if (hasLocalSession) {
            // If we have a session token but can't determine role, try to redirect based on URL hint
            const path = window.location.pathname;
            if (path.includes('admin')) {
              sessionStorage.setItem('userRole', 'admin');
              return <Navigate to="/admin" replace />;
            } else if (path.includes('trainer')) {
              sessionStorage.setItem('userRole', 'formateur');
              return <Navigate to="/trainer" replace />;
            } else if (path.includes('student')) {
              sessionStorage.setItem('userRole', 'etudiant');
              return <Navigate to="/student" replace />;
            }
          }
        } catch (e) {
          // Ignore errors
        }
      }
      
      // If no stored role or not matching, direct to dashboard
      return <Navigate to="/dashboard" replace />;
    } catch (e) {
      // If error accessing storage, redirect to dashboard
      console.error('Error checking stored role:', e);
      return <Navigate to="/dashboard" replace />;
    }
  }

  // If not loading and no user, redirect to login
  if (!isLoading && !user) {
    return <Navigate to="/login" replace />;
  }

  // If not loading and user has no role, redirect to dashboard
  if (!isLoading && !userRole) {
    return <Navigate to="/dashboard" replace />;
  }

  // If not loading and role doesn't match, redirect to appropriate dashboard
  if (!isLoading && userRole !== role) {
    if (userRole === 'admin') {
      return <Navigate to="/admin" replace />;
    } else if (userRole === 'formateur') {
      return <Navigate to="/trainer" replace />;
    } else if (userRole === 'etudiant') {
      return <Navigate to="/student" replace />;
    }
  }

  // If role matches, render the children routes
  return <Outlet />;
};

export default RoleBasedRoute;